import React, { useState, useCallback, useEffect, useRef } from "react";
import GameSummaryDialog from "@/components/GameSummaryDialog";
import { useDragScroll } from "@/hooks/useDragScroll";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import DiceBox from "@3d-dice/dice-box";
import { ArrowLeft, Dices, Plus, Minus, Trash2, RotateCcw, Trophy, Shuffle, Crown, Play, Square, Timer, Users as UsersIcon, Clock, CheckCircle, ChevronRight, UserPlus } from "lucide-react";

// ---------- Utility Hooks & Formats ----------
const formatTime = (totalSeconds) => {
  if (isNaN(totalSeconds)) return "00:00";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const diceStyles = `
#dice-canvas {
  position: fixed;
  inset: 0;
  pointer-events: none;
}
#dice-canvas .dice-box-canvas {
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  display: block;
}
@keyframes dice-roll-3d {
  0% { transform: rotateX(0deg) rotateY(0deg) translate3d(0, 0, 0); }
  30% { transform: rotateX(360deg) rotateY(180deg) translate3d(-20px, -50px, 80px); }
  70% { transform: rotateX(720deg) rotateY(540deg) translate3d(20px, -20px, 40px); }
  100% { transform: rotateX(1080deg) rotateY(720deg) translate3d(0, 0, 0); }
}
@keyframes dice-faux-3d {
  0% { transform: rotateX(0deg) rotateY(0deg) rotateZ(0deg) translate3d(0, 0, 0) scale(1); filter: blur(0px); }
  30% { transform: rotateX(360deg) rotateY(360deg) rotateZ(90deg) translate3d(-25px, -50px, 0) scale(1.5); filter: blur(1px); }
  70% { transform: rotateX(1080deg) rotateY(720deg) rotateZ(180deg) translate3d(25px, -15px, 0) scale(1.2); filter: blur(1px); }
  100% { transform: rotateX(1800deg) rotateY(1080deg) rotateZ(360deg) translate3d(0, 0, 0) scale(1); filter: blur(0px); }
}
`;

// Dice icons (SVG paths) for realism
const DiceIcons = {
  4: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"><path d="m12 2 9 16H3z"/><path d="M12 2v16"/><path d="m3 18 9-5 9 5"/></svg>,
  6: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/></svg>,
  8: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"><path d="m12 2 9 10-9 10-9-10z"/><path d="M3 12h18"/><path d="m12 2-4 10 4 10"/><path d="m12 2 4 10-4 10"/></svg>,
  10: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"><path d="m12 2 8 8-8 12-8-12z"/><path d="M4 10h16"/><path d="m12 2-4 8 4 12"/><path d="m12 2 4 8-4 12"/></svg>,
  12: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"><path d="m12 2 8 5v10l-8 5-8-5V7z"/><path d="M12 2v20"/><path d="m4 7 16 10"/><path d="m20 7-16 10"/></svg>,
  20: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"><path d="m12 2 9 6v8l-9 6-9-6V8z"/><path d="m12 2-5 9 5 11 5-11z"/><path d="m3 8 9 3 9-3"/><path d="m3 16 9-3 9 3"/></svg>,
  100: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/></svg>
};
// D3 removed: not supported by @3d-dice/dice-box theme (no 3D model available)
const DIE_TYPES = [4, 6, 8, 10, 12, 20, 100];

// ---------- Components ----------

function DiceRoller() {
  const [diceSelection, setDiceSelection] = useState({ 6: 1 });
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [isRolling, setIsRolling] = useState(false);
  const [diceBox, setDiceBox] = useState(null);
  const [dbLoading, setDbLoading] = useState(true);
  const [dbError, setDbError] = useState("");

  useEffect(() => {
    let box = null;
    let boxReady = false;
    let cancelled = false;

    const safeClear = (instance) => {
      if (!instance || typeof instance.clear !== "function") return;
      try {
        instance.clear();
      } catch (err) {
        console.warn("Skip clear() su DiceBox non inizializzato:", err);
      }
    };

    const loadDiceBox = async () => {
      try {
        const assetPathCandidates = ["/assets/", "/assets/dice-box/", "/assets/assets/"];
        let lastError = null;

        for (const assetPath of assetPathCandidates) {
          try {
            // Riduce la scala dei dadi su mobile per una migliore visualizzazione
            const isMobile = window.innerWidth < 768;
            box = new DiceBox({
              container: "#dice-canvas",
              assetPath,
              offscreen: false,
              scale: isMobile ? 6 : 10,
              spinForce: 8,
              throwForce: 8,
              startingHeight: 15,
              settleTimeout: 5000
            });
            await box.init();
            if (cancelled) {
              safeClear(box);
              return;
            }
            boxReady = true;
            setDiceBox(box);
            setDbError("");
            return;
          } catch (err) {
            lastError = err;
            console.error(`Errore init DiceBox con assetPath ${assetPath}:`, err);
            safeClear(box);
            box = null;
            boxReady = false;
          }
        }

        throw lastError || new Error("init-failed");
      } catch (err) {
        console.error("Errore caricamento DiceBox 3D:", err);
        const reason = err?.message ? ` (${err.message})` : "";
        setDbError(`3D non disponibile su questo browser/ambiente${reason}`);
      } finally {
        setDbLoading(false);
      }
    };
    loadDiceBox();

    return () => {
      cancelled = true;
      if (boxReady) safeClear(box);
    };
  }, []);

  const setDiceCount = (type, val) => {
    setDiceSelection(prev => {
      const next = { ...prev };
      if (val <= 0 || isNaN(val)) {
        delete next[type];
        return next;
      }
      
      let otherTotal = 0;
      for (const [k, v] of Object.entries(prev)) {
        if (Number(k) !== Number(type)) {
          otherTotal += v;
        }
      }
      
      const maxAllowed = Math.max(0, 10 - otherTotal);
      const finalVal = Math.min(val, maxAllowed);
      
      if (finalVal <= 0) {
        delete next[type];
      } else {
        next[type] = finalVal;
      }
      return next;
    });
  };

  const roll = async () => {
    if (!diceBox) return;
    const typesToRoll = Object.keys(diceSelection).map(Number);
    if (typesToRoll.length === 0) return;

    // Keep the physics world aligned with current viewport before each throw.
    diceBox.resizeWorld();
    
    setIsRolling(true);
    setResults([]);

    // Importante: Passare un array di stringhe a diceBox per fargli lanciare gruppi diversi assieme
    const notations = typesToRoll.map(type => `${diceSelection[type]}d${type}`);
    const summaryStr = notations.join(" + ");
    
    try {
      // Usiamo Promise.race come paracadute di sicurezza nel caso il motore fisico si inceppi
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("Timeout")), 6000)
      );
      
      const rollResults = await Promise.race([
        diceBox.roll(notations),
        timeoutPromise
      ]);
      
      let allRolls = [];
      if (Array.isArray(rollResults)) {
        rollResults.forEach(group => {
           if (group.rolls && Array.isArray(group.rolls)) {
             group.rolls.forEach(r => {
                let val = parseInt(r.value, 10);
                // Fallback for compound dice like d100 if the library fails to sum them
                if (isNaN(val) && Array.isArray(r.rolls)) {
                  val = r.rolls.reduce((acc, sub) => acc + (parseInt(sub.value, 10) || 0), 0);
                  if (val === 0 && (group.sides == 100 || r.sides == 100)) val = 100;
                }
                allRolls.push({ type: group.sides || r.sides, value: isNaN(val) ? "?" : val });
             });
           } else {
              const val = parseInt(group.value, 10);
              allRolls.push({ type: group.sides, value: isNaN(val) ? "?" : val });
           }
        });
      }

      setResults(allRolls);
      const total = allRolls.reduce((s, r) => s + (typeof r.value === "number" ? r.value : 0), 0);
      setHistory(prev => [{ rolls: allRolls, total, summary: summaryStr, id: Date.now() }, ...prev.slice(0, 4)]);
      
      // I dadi spariscono dopo poco per finire nel riquadro in basso
      setTimeout(() => {
        diceBox.clear();
      }, 1500);

    } catch (err) {
      console.error("Errore lancio dadi:", err);
      // Pulisce lo schermo in caso di errore per sbloccare i dadi incastrati
      diceBox.clear();
    } finally {
      setIsRolling(false);
    }
  };

  const total = results.reduce((s, r) => s + (typeof r.value === "number" ? r.value : 0), 0);

  return (
    <section className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 sm:p-6 flex flex-col h-full z-10 relative">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#F4DAD3] dark:bg-[#2E1A18] flex items-center justify-center text-[#C8553D]">
            <Dices size={16} strokeWidth={2} />
          </div>
          <h2 className="font-display text-lg font-semibold text-[#2D2A26] dark:text-[#F5F1EA]">Dadi 3D</h2>
        </div>
        <button onClick={() => setDiceSelection({})} className="text-xs text-[#757169] dark:text-[#B8B2AA] hover:text-[#C8553D] transition-colors"><RotateCcw size={14}/></button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {DIE_TYPES.map(d => (
          <div key={d} className={`flex flex-col gap-2 p-3 rounded-xl border transition-colors ${diceSelection[d] ? "bg-[#F5F1EA] dark:bg-[#1A1A1A] border-[#C8553D]/30" : "bg-transparent border-[#EAE3D9] dark:border-[#2A2A2A]"}`}>
            <div className="flex items-center gap-2">
              <div className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 transition-all p-1.5 ${diceSelection[d] ? "bg-[#C8553D] text-white" : "bg-[#F5F1EA] dark:bg-[#1A1A1A] text-[#757169] dark:text-[#B8B2AA]"}`}>
                {DiceIcons[d] || `d${d}`}
              </div>
              <span className="text-xs font-bold uppercase text-[#757169] dark:text-[#B8B2AA]">d{d}</span>
            </div>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min="0"
              max="10"
              step="1"
              value={diceSelection[d] || ""}
              onChange={(e) => setDiceCount(d, parseInt(e.target.value, 10))}
              placeholder="0"
              className="w-full h-12 bg-white dark:bg-[#0A0A0A] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-lg text-center text-xl font-bold text-[#2D2A26] dark:text-[#F5F1EA] outline-none focus:border-[#C8553D] focus:ring-2 focus:ring-[#C8553D]/20 transition-all"
            />
          </div>
        ))}
      </div>

      <button onClick={roll} disabled={isRolling || dbLoading || Object.keys(diceSelection).length === 0} className="w-full h-10 rounded-xl bg-[#C8553D] hover:bg-[#A6432E] active:scale-[0.98] text-[#FDFBF7] font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50">
        <Dices size={16} className={isRolling ? "animate-spin" : ""} /> {dbLoading ? "Caricamento 3D..." : "Lancia"}
      </button>
      {dbError && <p className="mt-2 text-xs text-[#C8553D] dark:text-[#F08A78]">{dbError}</p>}

      {results.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#EAE3D9] dark:border-[#2A2A2A]">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {results.map((r, i) => (
              <div key={i} className={`relative w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold border-2 animate-in zoom-in ${r.value === r.type ? "bg-[#DDE7DC] dark:bg-[#1A2E20] border-[#2B593F] text-[#2B593F] dark:text-[#8FC79F]" : r.value === 1 ? "bg-[#F4DAD3] dark:bg-[#2E1A18] border-[#C8553D] text-[#C8553D]" : "bg-[#F5F1EA] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] text-[#2D2A26] dark:text-[#F5F1EA]"}`} style={{ animationDelay: `${i * 0.05}s` }}>
                {r.value}
              </div>
            ))}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-2xl font-bold text-[#C8553D]">{total}</span>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------- Random Draw ----------
function RandomDraw({ players, setPlayers }) {
  const [result, setResult] = useState(null);
  const [spinning, setSpinning] = useState(false);

  const draw = (type) => {
    if (players.length < 1) return;
    setSpinning(true);
    setResult(null);
    setTimeout(() => {
      if (type === "single") {
        const picked = players[Math.floor(Math.random() * players.length)];
        setResult({ type: "single", data: picked });
      } else {
        const shuffled = [...players].sort(() => Math.random() - 0.5);
        setPlayers(shuffled);
        setResult({ type: "order", data: shuffled });
      }
      setSpinning(false);
    }, 600);
  };

  return (
    <section className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 sm:p-6 flex flex-col h-full">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 rounded-lg bg-[#E5E0F0] dark:bg-[#221A35] flex items-center justify-center text-[#5A4A8A] dark:text-[#B0A0D8]">
          <Shuffle size={16} strokeWidth={2} />
        </div>
        <h2 className="font-display text-lg font-semibold text-[#2D2A26] dark:text-[#F5F1EA]">Sorteggio</h2>
      </div>
      
      <p className="text-xs text-[#757169] dark:text-[#B8B2AA] mb-4">Utilizza i partecipanti in alto per fare il sorteggio.</p>

      <div className="flex gap-3 mb-6">
        <button onClick={() => draw("single")} disabled={spinning || players.length < 1} className="flex-1 h-10 rounded-xl bg-[#5A4A8A] hover:bg-[#483B70] text-white font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          <Crown size={14} /> Primo
        </button>
        <button onClick={() => draw("order")} disabled={spinning || players.length < 2} className="flex-1 h-10 rounded-xl bg-[#F5F1EA] dark:bg-[#1A1A1A] text-[#4A4742] dark:text-[#F5F1EA] border border-[#EAE3D9] dark:border-[#2A2A2A] font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-[#EAE3D9] dark:hover:bg-[#222]">
          <Shuffle size={14} /> Ordine
        </button>
      </div>

      <div className="mt-auto border-t border-[#EAE3D9] dark:border-[#2A2A2A] pt-4 min-h-[100px] flex items-center justify-center">
        {spinning && <Shuffle className="animate-spin text-[#5A4A8A] dark:text-[#B0A0D8]" size={24} />}
        {!spinning && result?.type === "single" && (
          <div className="text-center animate-in zoom-in">
            <Crown size={24} className="mx-auto text-[#5A4A8A] dark:text-[#B0A0D8] mb-1" />
            <p className="font-display text-xl font-bold text-[#2D2A26] dark:text-[#F5F1EA]">{result.data.name}</p>
          </div>
        )}
        {!spinning && result?.type === "order" && (
          <ol className="w-full space-y-1.5 animate-in fade-in">
            {result.data.slice(0, 3).map((p, i) => (
              <li key={p.id} className="flex items-center gap-2 p-1.5 rounded-lg bg-[#F5F1EA] dark:bg-[#1A1A1A]">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border border-[#EAE3D9] dark:border-[#333] ${i===0 ? "bg-[#5A4A8A] text-white border-none" : "bg-white dark:bg-[#222] text-[#757169] dark:text-[#B8B2AA]"}`}>{i+1}</span>
                <span className={`text-xs ${i===0 ? "text-[#5A4A8A] dark:text-[#B0A0D8] font-bold" : "text-[#4A4742] dark:text-[#F5F1EA]"}`}>{p.name}</span>
              </li>
            ))}
            {result.data.length > 3 && <p className="text-center text-[10px] text-[#757169] dark:text-[#B8B2AA] mt-1">...e altri {result.data.length - 3}</p>}
          </ol>
        )}
      </div>
    </section>
  );
}

const Scoreboard = React.forwardRef(function Scoreboard({ players }, ref) {
  const scrollProps = useDragScroll();
  const [columns, setColumns] = useState([{ id: 1, title: "M1" }]);
  const [scores, setScores] = useState({});
  const [nextColId, setNextColId] = useState(2);

  const addColumn = () => {
    setColumns(c => [...c, { id: nextColId, title: `M${nextColId}` }]);
    setNextColId(n => n + 1);
  };

  const removeColumn = (id) => {
    setColumns(c => c.filter(col => col.id !== id));
    setScores(s => {
      const next = { ...s };
      Object.keys(next).forEach(k => { if (k.endsWith(`-${id}`)) delete next[k]; });
      return next;
    });
  };

  const setScore = (playerId, colId, value) => {
    setScores(s => ({ ...s, [`${playerId}-${colId}`]: value }));
  };

  const getScore = (playerId, colId) => scores[`${playerId}-${colId}`] ?? "";

  const getTotal = (playerId) => columns.reduce((sum, col) => {
    const v = parseFloat(scores[`${playerId}-${col.id}`]);
    return sum + (isNaN(v) ? 0 : v);
  }, 0);

  const winner = players.length > 1 ? players.reduce((best, p) => getTotal(p.id) > getTotal(best.id) ? p : best, players[0]) : null;

  // Expose computed scores and winner to parent via ref
  React.useImperativeHandle(ref, () => ({
    getResults: () => {
      const playerScores = players.map(p => ({
        player_id: p.id,
        player_name: p.name,
        score: getTotal(p.id),
      }));
      const hasAnyScore = playerScores.some(s => s.score !== 0);
      return {
        scores: hasAnyScore ? playerScores : [],
        winner: (hasAnyScore && winner) ? winner : null,
      };
    }
  }));

  return (
    <section className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 sm:p-6 w-full mt-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#DDE7DC] dark:bg-[#1A2E20] flex items-center justify-center text-[#2B593F] dark:text-[#8FC79F]">
            <Trophy size={16} strokeWidth={2} />
          </div>
          <h2 className="font-display text-lg font-semibold text-[#2D2A26] dark:text-[#F5F1EA]">Punti</h2>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button onClick={addColumn} className="h-9 px-3 rounded-lg border border-[#EAE3D9] dark:border-[#2A2A2A] text-xs font-semibold text-[#2D2A26] dark:text-[#F5F1EA] hover:bg-[#F5F1EA] dark:hover:bg-[#1A1A1A] transition-colors flex items-center gap-1">
            <Plus size={14}/> Manche
          </button>
        </div>
      </div>

      <div {...scrollProps} className="overflow-x-auto pb-2 custom-scrollbar">
        {players.length === 0 ? (
          <div className="text-center py-6 border-2 border-dashed border-[#EAE3D9] dark:border-[#2A2A2A] rounded-xl text-[#757169] dark:text-[#B8B2AA] text-xs">Aggiungi partecipanti in alto per iniziare.</div>
        ) : (
          <table className="w-full text-sm min-w-[400px]">
            <thead>
              <tr>
                <th className="p-1 w-12"></th>
                {players.map(p => {
                  const isWinner = winner?.id === p.id && getTotal(p.id) > 0;
                  return (
                    <th key={p.id} className="p-1 min-w-[80px] group text-center">
                      <div className="flex flex-col items-center gap-1 relative">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden border-2 shadow-sm ${isWinner ? 'border-[#2B593F] dark:border-[#8FC79F] bg-[#DDE7DC] dark:bg-[#1A2E20]' : 'border-white dark:border-[#222] bg-[#F5F1EA] dark:bg-[#1A1A1A]'}`}>
                          {p.avatar ? <img src={`https://api.dicebear.com/9.x/adventurer/svg?seed=${p.avatar}`} className="w-full h-full object-cover"/> : <span className={`font-bold text-xs ${isWinner ? 'text-[#2B593F] dark:text-[#8FC79F]' : 'text-[#757169] dark:text-[#B8B2AA]'}`}>{p.name.charAt(0)}</span>}
                        </div>
                        <span className={`font-bold text-[10px] truncate max-w-[60px] ${isWinner ? 'text-[#2B593F] dark:text-[#8FC79F]' : 'text-[#2D2A26] dark:text-[#F5F1EA]'}`}>{p.name}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {columns.map(col => (
                <tr key={col.id} className="border-t border-[#F5F1EA] dark:border-[#1A1A1A] group">
                  <td className="p-1 relative">
                    <input 
                      value={col.title} 
                      onChange={(e) => setColumns(c => c.map(x => x.id === col.id ? { ...x, title: e.target.value } : x))} 
                      className="text-[10px] uppercase font-bold text-[#757169] dark:text-[#B8B2AA] w-full text-center bg-transparent focus:outline-none focus:text-[#2D2A26] dark:focus:text-[#F5F1EA] transition-colors"
                    />
                    {columns.length > 1 && (
                      <button onClick={() => removeColumn(col.id)} className="absolute left-0 top-1/2 -translate-y-1/2 text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={12}/></button>
                    )}
                  </td>
                  {players.map(p => (
                    <td key={p.id} className="p-1">
                      <input 
                        type="number" 
                        value={getScore(p.id, col.id)} 
                        onChange={(e) => setScore(p.id, col.id, e.target.value)} 
                        className="w-full text-center h-8 bg-[#FDFBF7] dark:bg-[#181818] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-lg font-semibold text-sm text-[#2D2A26] dark:text-[#F5F1EA] focus:outline-none focus:border-[#2B593F]" 
                        placeholder="-" 
                      />
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="p-1 pt-3 text-center text-[9px] uppercase font-bold text-[#757169] dark:text-[#B8B2AA]">Totale</td>
                {players.map(p => {
                  const total = getTotal(p.id);
                  const isWinner = winner?.id === p.id && total > 0;
                  return (
                    <td key={p.id} className="p-1 pt-3 text-center border-t border-[#EAE3D9] dark:border-[#2A2A2A]">
                      <span className={`font-display text-lg font-bold ${isWinner ? "text-[#2B593F] dark:text-[#8FC79F]" : "text-[#2D2A26] dark:text-[#F5F1EA]"}`}>{total}</span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        )}
      </div>
      {Object.keys(scores).length > 0 && (
        <div className="mt-4 flex justify-end">
           <button onClick={() => { if(window.confirm("Azzerare tutti i punteggi?")) setScores({}); }} className="text-xs text-[#757169] dark:text-[#B8B2AA] hover:text-[#C8553D] flex items-center gap-1 transition-colors">
            <RotateCcw size={12}/> Svuota Punteggi
          </button>
        </div>
      )}
    </section>
  );
});

// ---------- Participant Manager ----------
function ParticipantManager({ players, setPlayers, users }) {
  const [customName, setCustomName] = useState("");

  const addPlayerFromDropdown = (e) => {
    const val = e.target.value;
    if (!val) return;
    const isUserId = users.find(u => u.id === val);
    if(isUserId && !players.find(p => p.id === isUserId.id)) {
      setPlayers(p => [...p, { id: isUserId.id, name: isUserId.name, avatar: isUserId.avatar }]);
    }
    e.target.value = "";
  };

  const addCustomPlayer = async (e) => {
    e.preventDefault();
    const val = customName.trim();
    if (!val) return;
    
    try {
      const res = await api.get(`/check-profanity?text=${encodeURIComponent(val)}`);
      if (res.data.has_profanity) {
        alert("Il nome inserito contiene linguaggio non consentito.");
        return;
      }
    } catch(err) {
      console.error(err);
    }

    setPlayers(p => [...p, { id: Date.now().toString(), name: val, avatar: null }]);
    setCustomName("");
  };

  const removePlayer = (id) => {
    if(!window.confirm("Rimuovere questo giocatore?")) return;
    setPlayers(p => p.filter(pl => pl.id !== id));
  };

  return (
    <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 mb-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#EAE3D9] dark:bg-[#2A2A2A] flex items-center justify-center text-[#757169] dark:text-[#B8B2AA]">
          <UsersIcon size={16} strokeWidth={2} />
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold text-[#2D2A26] dark:text-[#F5F1EA] leading-none">Partecipanti</h2>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#757169] dark:text-[#B8B2AA] mt-1">Gestisci chi gioca</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {players.map(p => (
          <div key={p.id} className="flex items-center gap-2 bg-[#F5F1EA] dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-full pl-2 pr-3 py-1">
            <div className="w-6 h-6 rounded-full bg-white dark:bg-[#222] border border-[#EAE3D9] dark:border-[#333] flex items-center justify-center overflow-hidden flex-shrink-0">
              {p.avatar ? <img src={`https://api.dicebear.com/9.x/adventurer/svg?seed=${p.avatar}`} alt="Avatar" className="w-full h-full object-cover"/> : <span className="text-[10px] font-bold text-[#757169] dark:text-[#B8B2AA]">{p.name.charAt(0)}</span>}
            </div>
            <span className="text-xs font-bold text-[#2D2A26] dark:text-[#F5F1EA] truncate max-w-[100px]">{p.name}</span>
            <button onClick={() => removePlayer(p.id)} className="text-[#C0BAB2] dark:text-[#555] hover:text-[#EF4444] dark:hover:text-[#EF4444] ml-1"><Trash2 size={12}/></button>
          </div>
        ))}
        {players.length === 0 && <span className="text-sm text-[#757169] dark:text-[#B8B2AA] py-1">Nessun partecipante.</span>}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 border-t border-[#EAE3D9] dark:border-[#2A2A2A] pt-4">
        <select onChange={addPlayerFromDropdown} className="flex-1 h-10 px-3 rounded-xl bg-[#F5F1EA] dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] text-sm text-[#2D2A26] dark:text-[#F5F1EA] focus:outline-none focus:border-[#C8553D]">
          <option value="">+ Iscritto...</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <form onSubmit={addCustomPlayer} className="flex-1 flex gap-2">
           <input type="text" value={customName} onChange={e => setCustomName(e.target.value)} placeholder="Aggiungi ospite..." className="w-full h-10 px-3 rounded-xl bg-[#F5F1EA] dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] text-sm text-[#2D2A26] dark:text-[#F5F1EA] focus:outline-none focus:border-[#C8553D]" />
           <button type="submit" className="h-10 px-3 rounded-xl bg-[#F5F1EA] dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] hover:bg-[#EAE3D9] dark:hover:bg-[#222] text-[#2D2A26] dark:text-[#F5F1EA]"><UserPlus size={16}/></button>
        </form>
      </div>
    </div>
  );
}

// ---------- Page with Assistant Logic ----------

export default function UtilityPage() {
  const [users, setUsers] = useState([]);
  const [tables, setTables] = useState([]);
  
  // Session State
  const [activeTable, setActiveTable] = useState(null);
  const [players, setPlayers] = useState([]);
  
  // Timers State
  const [phase, setPhase] = useState("idle"); // idle, explanation, play, finished
  const [phaseTime, setPhaseTime] = useState(0); // seconds
  const [playerTimes, setPlayerTimes] = useState({}); // { id: seconds }
  const [activePlayerIndex, setActivePlayerIndex] = useState(-1);
  const [explanationTime, setExplanationTime] = useState(0); // saved explanation seconds
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [sessionSummaryData, setSessionSummaryData] = useState(null);
  const scoreboardRef = useRef(null);

  // Fetch Data
  useEffect(() => {
    api.get("/users").then(res => setUsers(res.data)).catch(console.error);
    api.get("/tables").then(res => setTables(res.data)).catch(console.error);
  }, []);

  // Handle Table Selection
  const handleSelectTable = (e) => {
    const tId = e.target.value;
    if (!tId) {
      setActiveTable(null);
      setPlayers([]);
      setPhase("idle");
      return;
    }
    const t = tables.find(x => x.id === tId);
    setActiveTable(t);
    if(t && t.players) {
      setPlayers(t.players.map(p => ({ id: p.id, name: p.name, avatar: p.avatar })));
    }
    // Reset timers
    setPhase("idle");
    setPhaseTime(0);
    setPlayerTimes({});
    setActivePlayerIndex(-1);
    setExplanationTime(0);
    setSessionSummaryData(null);
  };

  // Timer Tick
  useEffect(() => {
    if (phase === "idle" || phase === "finished") return;
    
    const interval = setInterval(() => {
      setPhaseTime(prev => prev + 1);
      
      // If playing and a player's turn is active, increment their clock
      if (phase === "play" && activePlayerIndex >= 0 && players[activePlayerIndex]) {
        const activeId = players[activePlayerIndex].id;
        setPlayerTimes(prev => ({
          ...prev,
          [activeId]: (prev[activeId] || 0) + 1
        }));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, activePlayerIndex, players]);

  // Session Controls
  const startExplanation = () => { setPhase("explanation"); setPhaseTime(0); };
  const startPlay = () => { setExplanationTime(phaseTime); setPhase("play"); setPhaseTime(0); setActivePlayerIndex(0); };
  const nextTurn = () => {
    if (players.length === 0) return;
    setActivePlayerIndex((prev) => (prev + 1) % players.length);
  };
  const finishSession = () => {
    // Capture measured play time in minutes
    const playTimeMinutes = Math.max(1, Math.round(phaseTime / 60));
    const explanationMins = Math.round(explanationTime / 60);
    // Read scores and winner from the scoreboard
    const results = scoreboardRef.current?.getResults() || { scores: [], winner: null };
    // Convert array to map format expected by GameSummaryDialog
    const scoresMap = {};
    for (const s of results.scores) {
      scoresMap[s.player_id] = { player_id: s.player_id, player_name: s.player_name, score: s.score };
    }
    setSessionSummaryData({
      playTimeMinutes,
      explanationMins,
      scoresMap,
      winnerId: results.winner ? results.winner.id : "none",
    });
    setPhase("finished");
    // Open save-to-history dialog if a table is linked
    if (activeTable) setSummaryOpen(true);
  };

  // Randomize Order
  const shufflePlayers = () => {
    const shuffled = [...players].sort(() => Math.random() - 0.5);
    setPlayers(shuffled);
    if(phase === "play") setActivePlayerIndex(0);
  };

  // Calculations for Report
  const totalPlayTime = Object.values(playerTimes).reduce((a,b)=>a+b, 0) || phaseTime;
  const expectedPlayTime = activeTable ? activeTable.play_time * 60 : 0;
  const timeDiff = totalPlayTime - expectedPlayTime; // positive means overtime
  
  let fastestPlayer = null;
  let slowestPlayer = null;
  if (players.length > 0 && Object.keys(playerTimes).length > 0) {
    const validPlayers = players.filter(p => playerTimes[p.id] !== undefined);
    if (validPlayers.length > 0) {
      fastestPlayer = validPlayers.reduce((min, p) => playerTimes[p.id] < playerTimes[min.id] ? p : min, validPlayers[0]);
      slowestPlayer = validPlayers.reduce((max, p) => playerTimes[p.id] > playerTimes[max.id] ? p : max, validPlayers[0]);
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] dark:bg-[#181818] flex flex-col">
      <style>{diceStyles}</style>
      <div id="dice-canvas" className="fixed top-0 left-0 w-full h-full pointer-events-none z-[100]"></div>
      <Header />
      <main className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#757169] hover:text-[#2D2A26] dark:text-[#B8B2AA] dark:hover:text-[#F5F1EA] mb-6 transition-colors">
          <ArrowLeft size={14} strokeWidth={2.5} /> Dashboard
        </Link>

        {/* --- LOBBY / TABLE SELECTOR --- */}
        <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between shadow-sm">
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="w-10 h-10 rounded-xl bg-[#2D2A26] dark:bg-[#F5F1EA] text-white dark:text-[#111111] flex items-center justify-center">
              <Timer size={20} />
            </div>
            <div className="flex-1">
              <h1 className="font-display text-xl font-bold text-[#2D2A26] dark:text-[#F5F1EA] leading-tight">Assistente di Sessione</h1>
              <p className="text-xs text-[#757169] dark:text-[#B8B2AA]">Seleziona un tavolo per attivare la regia.</p>
            </div>
          </div>
          <select 
            onChange={handleSelectTable} 
            value={activeTable?.id || ""}
            className="w-full md:w-72 h-11 px-3 rounded-xl bg-[#F5F1EA] dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] text-sm font-bold text-[#2D2A26] dark:text-[#F5F1EA] focus:outline-none focus:border-[#C8553D]"
          >
            <option value="">Nessun tavolo / Solo utility</option>
            {tables.map(t => <option key={t.id} value={t.id}>{t.game_name}</option>)}
          </select>
        </div>

        {/* --- PARTICIPANT MANAGER --- */}
        <ParticipantManager players={players} setPlayers={setPlayers} users={users} />

        {/* --- SESSION CONTROL (Visible only if table is selected) --- */}
        {activeTable && phase !== "finished" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Global Clock */}
            <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-6 shadow-sm relative overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase text-[#757169] dark:text-[#B8B2AA] tracking-wider">Cronometro Globale</span>
                <span className={`text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wider ${phase==='explanation' ? 'bg-[#D6E8F0] text-[#1E5C7A]' : phase==='play' ? 'bg-[#DDE7DC] text-[#2B593F]' : 'bg-[#EAE3D9] text-[#757169] dark:bg-[#1A1A1A] dark:text-[#B8B2AA]'}`}>
                  {phase === 'idle' ? 'Attesa' : phase === 'explanation' ? 'Spiegazione' : 'Partita'}
                </span>
              </div>
              <div className="text-center mb-6">
                <div className="font-display text-6xl font-black text-[#2D2A26] dark:text-[#F5F1EA] tracking-tighter">
                  {formatTime(phaseTime)}
                </div>
                {phase === 'explanation' && <p className="text-sm text-[#757169] dark:text-[#B8B2AA] mt-1">Previsti: {activeTable.explanation_time} min</p>}
                {phase === 'play' && <p className="text-sm text-[#757169] dark:text-[#B8B2AA] mt-1">Previsti: {activeTable.play_time} min</p>}
              </div>
              <div className="flex gap-2 justify-center">
                {phase === "idle" && (
                  <>
                    <button onClick={startExplanation} className="px-5 py-2.5 bg-[#1E5C7A] text-white rounded-xl font-bold text-sm hover:bg-[#16435A] transition-colors">Inizia Spiegazione</button>
                    <button onClick={startPlay} className="px-5 py-2.5 bg-[#2B593F] text-white rounded-xl font-bold text-sm hover:bg-[#1D3C2A] transition-colors">Vai alla Partita</button>
                  </>
                )}
                {phase === "explanation" && (
                  <button onClick={startPlay} className="px-5 py-2.5 bg-[#2B593F] text-white rounded-xl font-bold text-sm hover:bg-[#1D3C2A] transition-colors flex items-center gap-2"><Play size={16}/> Inizia Partita</button>
                )}
                {phase === "play" && (
                  <button onClick={finishSession} className="px-5 py-2.5 bg-[#C8553D] text-white rounded-xl font-bold text-sm hover:bg-[#A6432E] transition-colors flex items-center gap-2"><Square size={16}/> Concludi Partita</button>
                )}
              </div>
            </div>

            {/* Turn Manager */}
            <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-6 shadow-sm flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase text-[#757169] dark:text-[#B8B2AA] tracking-wider">Orologio a Turni</span>
                {phase === 'play' && <button onClick={shufflePlayers} className="text-xs text-[#5A4A8A] dark:text-[#B0A0D8] flex items-center gap-1 hover:underline"><Shuffle size={12}/> Sorteggia Ordine</button>}
              </div>

              {players.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-sm text-[#757169] dark:text-[#B8B2AA]">Aggiungi partecipanti sopra per i turni.</div>
              ) : phase !== 'play' ? (
                <div className="flex-1 flex items-center justify-center text-sm text-[#757169] dark:text-[#B8B2AA]">L'orologio a turni parte durante la partita.</div>
              ) : (
                <div className="flex-1 flex flex-col">
                  {/* Current Player Card */}
                  <div className="bg-[#F5F1EA] dark:bg-[#1A1A1A] rounded-xl p-4 mb-4 flex items-center gap-4 border border-[#EAE3D9] dark:border-[#2A2A2A]">
                    <div className="w-14 h-14 rounded-full bg-white dark:bg-[#222] border-2 border-[#5A4A8A] flex items-center justify-center overflow-hidden flex-shrink-0">
                      {players[activePlayerIndex]?.avatar ? <img src={`https://api.dicebear.com/9.x/adventurer/svg?seed=${players[activePlayerIndex].avatar}`} className="w-full h-full object-cover"/> : <span className="font-bold text-[#757169] dark:text-[#B8B2AA]">{players[activePlayerIndex]?.name.charAt(0)}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase font-bold text-[#5A4A8A] dark:text-[#B0A0D8] mb-0.5">Tocca a</p>
                      <p className="font-bold text-lg text-[#2D2A26] dark:text-[#F5F1EA] truncate">{players[activePlayerIndex]?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xl font-bold text-[#5A4A8A] dark:text-[#B0A0D8]">{formatTime(playerTimes[players[activePlayerIndex]?.id] || 0)}</p>
                    </div>
                  </div>

                  <button onClick={nextTurn} className="w-full h-14 bg-[#5A4A8A] hover:bg-[#483B70] text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 active:scale-95 transition-all shadow-md shadow-[#5A4A8A]/20">
                    <ChevronRight size={24}/> Passa il Turno
                  </button>
                  
                  {/* Next player preview */}
                  <p className="text-center text-xs text-[#757169] dark:text-[#B8B2AA] mt-3">
                    Prossimo: <span className="font-bold text-[#2D2A26] dark:text-[#F5F1EA]">{players[(activePlayerIndex + 1) % players.length]?.name}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* --- FINISHED REPORT --- */}
        {phase === "finished" && (
          <div className="bg-gradient-to-br from-[#1E1E1E] to-[#111111] border border-[#2A2A2A] rounded-3xl p-8 mb-6 text-white shadow-xl animate-in zoom-in">
            <div className="text-center mb-6">
              <CheckCircle size={40} className="mx-auto text-[#8FC79F] mb-3" strokeWidth={1.5} />
              <h2 className="font-display text-4xl font-black mb-1">Partita Conclusa!</h2>
              <p className="text-[#B8B2AA]">{activeTable?.game_name}</p>
            </div>

            {/* Winner hero */}
            {(() => {
              const summaryWinner = sessionSummaryData?.winnerId && sessionSummaryData.winnerId !== "none"
                ? sessionSummaryData.scoresMap?.[sessionSummaryData.winnerId]
                : null;
              if (!summaryWinner) return null;
              const winnerAvatar = players.find(p => p.id === sessionSummaryData.winnerId)?.avatar;
              return (
                <div className="relative flex flex-col items-center justify-center bg-gradient-to-b from-[#2B593F]/40 to-[#1A3528]/30 border border-[#8FC79F]/30 rounded-2xl px-6 py-6 mb-6 overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
                    <Trophy size={160} />
                  </div>
                  <Trophy size={28} className="text-[#F5E6BD] mb-3" />
                  <div className="w-20 h-20 rounded-full border-4 border-[#8FC79F] shadow-lg overflow-hidden bg-[#1A3528] mb-3 flex items-center justify-center">
                    {winnerAvatar
                      ? <img src={`https://api.dicebear.com/9.x/adventurer/svg?seed=${winnerAvatar}`} alt={summaryWinner.player_name} className="w-full h-full object-cover" />
                      : <span className="font-black text-3xl text-[#8FC79F]">{summaryWinner.player_name.charAt(0)}</span>
                    }
                  </div>
                  <p className="text-[10px] uppercase tracking-widest text-[#8FC79F] mb-1">Vincitore ai punti</p>
                  <p className="font-display text-5xl font-black text-white mb-1 drop-shadow-lg">{summaryWinner.player_name}</p>
                  <p className="text-2xl font-bold text-[#F5E6BD]">{summaryWinner.score} <span className="text-sm font-normal text-[#B8B2AA]">punti</span></p>
                </div>
              );
            })()}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/5 rounded-2xl p-5 text-center border border-white/10">
                <p className="text-[10px] uppercase tracking-widest text-[#B8B2AA] mb-2">Durata Partita</p>
                <p className="font-display text-3xl font-bold">{formatTime(totalPlayTime)}</p>
                <p className={`text-xs mt-2 ${timeDiff > 0 ? 'text-[#E8897A]' : 'text-[#8FC79F]'}`}>
                  {timeDiff > 0 ? `+${formatTime(timeDiff)} oltre il previsto` : `${formatTime(Math.abs(timeDiff))} in anticipo!`}
                </p>
              </div>
              <div className="bg-white/5 rounded-2xl p-5 text-center border border-white/10 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-2 text-[#F5E6BD] opacity-20"><Crown size={40}/></div>
                <p className="text-[10px] uppercase tracking-widest text-[#B8B2AA] mb-2 relative z-10">Il Fulmine</p>
                <p className="font-display text-2xl font-bold relative z-10">{fastestPlayer ? fastestPlayer.name : "-"}</p>
                <p className="text-xs text-[#8FC79F] mt-2 relative z-10">{fastestPlayer ? formatTime(playerTimes[fastestPlayer.id]) : ""}</p>
              </div>
              <div className="bg-white/5 rounded-2xl p-5 text-center border border-white/10">
                <p className="text-[10px] uppercase tracking-widest text-[#B8B2AA] mb-2">Il Pensatore</p>
                <p className="font-display text-2xl font-bold">{slowestPlayer ? slowestPlayer.name : "-"}</p>
                <p className="text-xs text-[#E8897A] mt-2">{slowestPlayer ? formatTime(playerTimes[slowestPlayer.id]) : ""}</p>
              </div>
            </div>

            <div className="text-center">
              <button onClick={() => { setPhase("idle"); setPhaseTime(0); setPlayerTimes({}); setActivePlayerIndex(-1); setExplanationTime(0); setSessionSummaryData(null); }} className="px-6 py-2.5 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-bold transition-colors">Nuova Sessione</button>
            </div>
          </div>
        )}

        {/* --- GRID (Dice & Scoreboard) --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <DiceRoller />
          <RandomDraw players={players} setPlayers={setPlayers} />
        </div>
        <Scoreboard ref={scoreboardRef} players={players} />
        {activeTable && sessionSummaryData && (
          <GameSummaryDialog
            table={{
              ...activeTable,
              play_time: sessionSummaryData.playTimeMinutes,
              explanation_time: sessionSummaryData.explanationMins,
              players: players,
            }}
            open={summaryOpen}
            onOpenChange={setSummaryOpen}
            onSaved={() => setSummaryOpen(false)}
            initialWinnerId={sessionSummaryData.winnerId}
            initialScores={sessionSummaryData.scoresMap}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}
